import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    Output,
} from "@angular/core";
import {CommonModule, NgOptimizedImage} from "@angular/common";
import {skip, take, delay} from "rxjs/operators";

// Material MDC Imports
import {MatCardModule} from "@angular/material/card";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MapService} from "../../../../viewer-components/services/internal-services/map.service";
import {SearchDisplayMode, SearchResultItem} from "../../../model";
import {ViewerConstants} from "../../../../shared/model/viewer-constants";
import {ClipboardModule} from "@angular/cdk/clipboard";
import {SharedComponentsModule} from "../../../../shared-components/shared-components.module";
import {SearchQueryService} from "../../../services/search";
import {
    VIEWER_SERVICE
} from "../../../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";


/**
 * Common search result card component
 * Used both in Grid view and Map sidebar to display individual search results
 */
@Component({
    selector: "app-search-result-card",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./search-result-card.component.html",
    styleUrls: ["./search-result-card.component.scss"],
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgOptimizedImage,
        ClipboardModule,
        SharedComponentsModule,
    ],
})
export class SearchResultCardComponent {
    private mapService = inject(MapService);

    private searchQueryService = inject(SearchQueryService);

    private viewerService = inject(VIEWER_SERVICE);

    private cdr = inject(ChangeDetectorRef);

    /** Search result item data */
    @Input() item!: SearchResultItem;

    @Input() index: number = 0;

    /** Current feedback state */
    @Input() feedback?: "up" | "down" | null = null;

    /** When false, hides the thumbs up/down feedback buttons entirely. */
    @Input() showFeedback = true;

    /** When false, hides the SVG bounding-box overlay. */
    @Input() showBbox = true;

    /** When false, hides the mask SVG overlay. */
    @Input() showMask = true;

    /** Fill color for the mask SVG overlay (hex or CSS color). */
    @Input() maskColor: string | null = null;

    @Input() imageHeight = 400;

    @Input() imageWidth = 400;

    @Input() viewPortSize: { width: number; height: number } = null;

    /** Event emitted when view details is clicked */
    @Output() viewDetails = new EventEmitter<SearchResultItem>();

    /** Event emitted when download is clicked */
    @Output() download = new EventEmitter<SearchResultItem>();

    /** Event emitted when share is clicked */
    @Output() share = new EventEmitter<SearchResultItem>();

    /** Event emitted when feedback is provided */
    @Output() feedbackChange = new EventEmitter<{ item: SearchResultItem; feedback: "up" | "down" }>();

    /**
     * Event emitted when fit is triggered (for scroll sync)
     */
    @Output() fitToItem = new EventEmitter<number>();

    /**
     * Gets the image URL for the result card
     */
    getImageUrl(): string {
        // When the item carries a specific source layer (e.g. detection results
        // referencing images[].metadata.layer), use it; otherwise fall back to
        // the broad layer list used by regular search results.
        const layer = this.item.imageLayer ?? "urban,urban-r,wide-area,graysky";
        const epsg = 4326;
        const params: Record<string, unknown> = {
            layer,
            wkt: this.item.geometry,
            srid: epsg,
            destinationDimensions: `${this.imageWidth},${this.imageHeight}`,
            attribution: false,
        };

        if (this.#isOblique(this.item.productType)) {
            params.orientation = this.translateDirection(this.item.productType);
            params.collection = this.item.collection;
            return this.mapService.extractAdvancedOrientedImageUrl(params);
        } else {
            params.orderBy = this.item.collection + ",layer-asc";
            return this.mapService.extractAdvancedOrthoImageUrl(params);
        }
    }

    #isOblique(text: string | null | undefined): boolean {
        return text?.startsWith("oblique-") ?? false;
    }

    translateDirection(text: string): string {
        if (!text) return ""
        if (!text.startsWith("oblique-")) return "2D";
        const direction = text.slice("oblique-".length);
        const orientations = ViewerConstants.COLLECTION_MODES.OBLIQUE.orientations;
        const match = Object.values(orientations).find(
            (o) => o.description.toLowerCase() === direction.toLowerCase()
        );
        return match?.name ?? direction;
    }


    /**
     * SVG polygon points (viewBox 0–100) for the bbox overlay. Read straight
     * from `item.overlayRect` — pre-computed upstream from `pixel-bbox +
     * bufferPixel`. Returns null when there's no overlay rect (regular
     * search cards).
     */
    getDrawingPolygonPoints(): string | null {
        const r = this.item?.overlayRect;
        if (!r) return null;
        const l = r.left.toFixed(3);
        const right = (r.left + r.width).toFixed(3);
        const t = r.top.toFixed(3);
        const b = (r.top + r.height).toFixed(3);
        return `${l},${t} ${right},${t} ${right},${b} ${l},${b} ${l},${t}`;
    }

    /**
     * Fits the map view to the item's geometry using the appropriate options.
     * Switches to MAP mode if currently in GRID mode.
     * Logs errors and emits project selection.
     */
    onFit(): void {
        const mode = this.searchQueryService.getCurrentDisplayMode();
        let fitOptions = this.getFitOptions(mode);
        if (mode === SearchDisplayMode.GRID) {
            this.searchQueryService.setSearchDisplayMode(SearchDisplayMode.MAP);
            this.fitToItem.emit(this.index);
        }

        this.viewerService.projectStatus$.pipe(skip(1), take(1), delay(300)).subscribe({
            next: (status) => {
                // Already moved
                if (!this.#isOblique(this.item.productType)) {
                    const aois = status.aois;
                    const aoi = aois.find((a) => a.getName() === this.item.collection);
                    if (aois && aois.length > 0 && status.centerAOI?.getName() !== aoi.getName()) {
                        this.viewerService.show2DProject(aoi);
                    }
                }
            },
        });

        this.mapService.discreteEnabled$.pipe(skip(1), take(1), delay(2000)).subscribe({
            next: (enabled) => {
                if (enabled) {
                    this.mapService.fitToWktPolygon(this.item.geometry, ViewerConstants.PROJECTIONS.WGS84, fitOptions);
                }
            }
        });

        if (this.#isOblique(this.item.productType)) {
            const aoi = this.viewerService.getProjectStatus().aois.find((a) => a.getName() === this.item.collection);
            this.viewerService.show3DProject(aoi, null, [this.item.centroid[0],this.item.centroid[1]], this.translateDirection(this.item.productType), ViewerConstants.PROJECTIONS.WGS84);
        } else {
            const mapStatus = this.mapService.getMapStatus();
            if (mapStatus.is3dMode) {
                this.viewerService.changeTo2D();
                this.viewerService.changeOrientation(ViewerConstants.COLLECTION_MODES.VERTICAL.orientations.N.name)
            }
            this.mapService.fitToWktPolygon(this.item.geometry, ViewerConstants.PROJECTIONS.WGS84, fitOptions);
        }
    }

    /**
     * Returns fit options for fitToWktPolygon based on the current display mode.
     * @returns {FitOptions | null} Fit options object or null if invalid.
     */
    private getFitOptions(mode): { size?: [number, number]; padding: number[]; duration: number } | null {
        if (mode === SearchDisplayMode.GRID) {
            if (
                this.viewPortSize &&
                typeof this.viewPortSize.width === "number" &&
                typeof this.viewPortSize.height === "number"
            ) {
                return {
                    size: [this.viewPortSize.height, this.viewPortSize.width],
                    padding: [10, 10, 10, 10],
                    duration: 0,
                };
            }
        }
        return {
            padding: [10, 10, 10, 10],
            duration: 0,
        };
    }

    /**
     * Handles thumbs up feedback
     */
    onThumbsUp(): void {
        const newFeedback = this.feedback === "up" ? null : "up";
        this.feedback = newFeedback;
        this.cdr.markForCheck();
        this.feedbackChange.emit({item: this.item, feedback: newFeedback});
    }

    /**
     * Handles thumbs down feedback
     */
    onThumbsDown(): void {
        const newFeedback = this.feedback === "down" ? null : "down";
        this.feedback = newFeedback;
        this.cdr.markForCheck();
        this.feedbackChange.emit({item: this.item, feedback: newFeedback});
    }
}
